import React, { useEffect, useState } from 'react';
import { Box, Text, Heading, Spinner, SpinnerSize, Button, Icon, ButtonSize } from '@bechtel/gds';
import { useEnvProvider, useAppUser, useConfig } from '@bechtel/client-common';
import { get, map } from 'lodash';
import { Trans } from '@lingui/macro';
import { RodRoom } from '@bechtel/types';
import { Map, MarkerType, Marker, useRodRoomListQuery } from '@bechtel/client-weld-common';
import { MapContainerStyled } from './styled';
import { ProjectAdminContainerStyled, ProjectAdminSubContainerStyled, ProjectAdminHeaderStyled } from '../styled';
import { RodRoomsItem } from './item';
import RodRoomEditIcon from '@bechtel/client-assets/dist/images/rod-room-edit-icon.svg';
import RodRoomIcon from '@bechtel/client-assets/dist/images/rod-room-icon.svg';
import RodRoomInactiveIcon from '@bechtel/client-assets/dist/images/rod-room-inactive-icon.svg';

const getMarkers = (rodRooms: RodRoom[], selectedRodRoomIds: Set<string>): Marker[] => {
  return map(rodRooms, (rodRoom) => {
    return {
      id: rodRoom.id,
      markerType: MarkerType.ROD_ROOM,
      position: { lat: rodRoom.location?.latitude, lng: rodRoom.location?.longitude },
      icon: selectedRodRoomIds.has(rodRoom.id) ? RodRoomEditIcon : rodRoom.isActive ? RodRoomIcon : RodRoomInactiveIcon,
    };
  });
};

export const RodRooms = () => {
  const [selectedRodRoomIds, setSelectedRodRoomIds] = useState<Set<string>>(new Set([]));
  const { envConfig } = useEnvProvider();
  const { features } = useConfig();
  const { user } = useAppUser();
  const { loading, data, error, refetch } = useRodRoomListQuery();
  const [markerList, setMarkerList] = useState<Marker[] | []>([]);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const rodRoomsList: RodRoom[] = get(data, 'rodRooms', []);

  useEffect(() => {
    const markers = getMarkers(rodRoomsList, selectedRodRoomIds);
    if (markers.length) {
      const mapCenter: google.maps.LatLngLiteral = user?.project?.projectConfiguration.geoLocation
        ? {
            lat: user.project.projectConfiguration.geoLocation?.latitude,
            lng: user.project.projectConfiguration.geoLocation?.longitude,
          }
        : { lat: markers[0]['position']['lat'], lng: markers[0]['position']['lng'] };
      setMarkerList(markers);
      setMapCenter(mapCenter);
    }
  }, [selectedRodRoomIds, rodRoomsList]);

  return (
    <ProjectAdminContainerStyled>
      <ProjectAdminHeaderStyled>
        <Text family="graphik" size="600" weight="600" color="white">
          <Trans>Rod Rooms</Trans>
        </Text>
      </ProjectAdminHeaderStyled>
      {error && (
        <Box
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'center' }}
          alignItems={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
        >
          <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
            <Text>
              <Trans>There was a problem loading the Rod Rooms.</Trans>
            </Text>
            <Text>
              <Trans>Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box>
            <Button size={ButtonSize.MEDIUM} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              Retry
            </Button>
          </Box>
        </Box>
      )}
      {!error && loading && (
        <Box
          flexGrow={{ sm: 1 }}
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          justifyContent={{ sm: 'center' }}
          data-testid="loading"
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!error && !loading && rodRoomsList.length && (
        <ProjectAdminSubContainerStyled mt={{ sm: '450' }} data-testid="module-data-container">
          <Box my={{ sm: '550' }} mx={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
            <Box>
              <Heading family="graphik" size="500" weight="600">
                <Trans>Manage Rod Rooms</Trans>
              </Heading>
            </Box>
            <Box mt={{ sm: '400' }}>
              <Text family="graphik" size="300">
                <Trans>Add, Manage Rod Rooms for a project</Trans>
              </Text>
            </Box>
            {features?.showMapView && markerList.length > 0 && (
              <MapContainerStyled mt={{ sm: '550' }}>
                <Map
                  apiKey={envConfig?.mapApiKey}
                  zoom={16}
                  center={mapCenter}
                  markers={markerList}
                  // setting the value using enum from google.maps.MapTypeId throwing google undefined error.needs debugging. With string, it's working fine.
                  mapTypeId="satellite"
                />
              </MapContainerStyled>
            )}
            {map(rodRoomsList, (rodRoom: RodRoom) => {
              return <RodRoomsItem key={rodRoom.id} rodRoom={rodRoom} setSelectedRodRoomIds={setSelectedRodRoomIds} />;
            })}
          </Box>
        </ProjectAdminSubContainerStyled>
      )}
    </ProjectAdminContainerStyled>
  );
};
